/* ==========================================================================
   suuhaa.js — 自作セクション共通JS
   ・すべて Custom Elements 内に閉じ込め、グローバルを汚さない
   ・要素の取得は this.querySelector(All) のみ(セクション内だけを対象にする)
   ========================================================================== */

/* --------------------------------------------------------------------------
   <suuhaa-hero-snail-mail>: リンゴをドラッグ / 矢印キーで動かす
   外部ライブラリは使わず、ネイティブの Pointer Events で実装
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  // 設定値はここに一元管理
  const suuhaa_heroConfig = {
    keyStep: 8, // 矢印キー1回で動く距離(px)
    keyStepShift: 32, // Shiftを押しながらのとき(px)
    draggingClass: 'suuhaa-hero-snail-mail__item--dragging',
  };

  // 矢印キーと移動方向の対応表
  const suuhaa_arrowKeys = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  };

  if (!customElements.get('suuhaa-hero-snail-mail')) {
    class SuuhaaHeroSnailMail extends HTMLElement {
      constructor() {
        super();
        this.suuhaa_offsets = new Map(); // リンゴごとの移動量 {x, y}(px)
        this.suuhaa_drag = null; // ドラッグ中の情報
        this.suuhaa_abort = null; // イベントリスナーまとめて解除用
        this.suuhaa_resizeObserver = null;
        this.suuhaa_stageWidth = 0;
      }

      connectedCallback() {
        this.suuhaa_abort = new AbortController();
        const signal = this.suuhaa_abort.signal;

        const items = this.querySelectorAll('[data-suuhaa-draggable]');
        items.forEach((item) => {
          this.suuhaa_offsets.set(item, { x: 0, y: 0 });
          item.addEventListener('pointerdown', (e) => this.suuhaa_onPointerDown(e, item), { signal });
          item.addEventListener('pointermove', (e) => this.suuhaa_onPointerMove(e), { signal });
          item.addEventListener('pointerup', (e) => this.suuhaa_onPointerEnd(e, item), { signal });
          item.addEventListener('pointercancel', (e) => this.suuhaa_onPointerEnd(e, item), { signal });
          item.addEventListener('keydown', (e) => this.suuhaa_onKeyDown(e, item), { signal });
        });

        // 画面幅が変わったとき、動かした分もステージの大きさに合わせて拡縮する
        const stage = this.querySelector('[data-suuhaa-stage]');
        if (stage && 'ResizeObserver' in window) {
          this.suuhaa_resizeObserver = new ResizeObserver(() => this.suuhaa_onResize(stage));
          this.suuhaa_resizeObserver.observe(stage);
        }
      }

      // テーマエディターでセクションが消えたとき等の後片付け
      disconnectedCallback() {
        if (this.suuhaa_abort) {
          this.suuhaa_abort.abort(); // 登録したイベントリスナーをすべて解除
          this.suuhaa_abort = null;
        }
        if (this.suuhaa_resizeObserver) {
          this.suuhaa_resizeObserver.disconnect();
          this.suuhaa_resizeObserver = null;
        }
        this.suuhaa_offsets.clear();
        this.suuhaa_drag = null;
        this.suuhaa_stageWidth = 0;
      }

      suuhaa_onPointerDown(event, item) {
        // マウスは左ボタンのみ。ドラッグ中の別の指は無視
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.suuhaa_drag) return;

        const rect = item.getBoundingClientRect();
        this.suuhaa_drag = {
          item,
          pointerId: event.pointerId,
          grabX: event.clientX - rect.left, // つかんだ位置(リンゴ内)
          grabY: event.clientY - rect.top,
        };
        item.setPointerCapture(event.pointerId); // 指/マウスが要素の外に出ても追従させる
        item.classList.add(suuhaa_heroConfig.draggingClass);
      }

      suuhaa_onPointerMove(event) {
        const drag = this.suuhaa_drag;
        if (!drag || drag.pointerId !== event.pointerId) return;

        const rect = drag.item.getBoundingClientRect();
        this.suuhaa_moveBy(
          drag.item,
          event.clientX - drag.grabX - rect.left,
          event.clientY - drag.grabY - rect.top
        );
      }

      suuhaa_onPointerEnd(event, item) {
        const drag = this.suuhaa_drag;
        if (!drag || drag.pointerId !== event.pointerId) return;

        if (item.hasPointerCapture(event.pointerId)) {
          item.releasePointerCapture(event.pointerId);
        }
        item.classList.remove(suuhaa_heroConfig.draggingClass);
        this.suuhaa_drag = null;
      }

      suuhaa_onKeyDown(event, item) {
        const dir = suuhaa_arrowKeys[event.key];
        if (!dir) return;

        event.preventDefault(); // 矢印キーでページがスクロールしないように
        const step = event.shiftKey ? suuhaa_heroConfig.keyStepShift : suuhaa_heroConfig.keyStep;
        this.suuhaa_moveBy(item, dir[0] * step, dir[1] * step);
      }

      // リンゴを (dx, dy) px 動かす。セクションの外に出ない範囲に制限する
      suuhaa_moveBy(item, dx, dy) {
        const bounds = this.getBoundingClientRect();
        const rect = item.getBoundingClientRect();

        const clampedX = Math.min(Math.max(dx, bounds.left - rect.left), bounds.right - rect.right);
        const clampedY = Math.min(Math.max(dy, bounds.top - rect.top), bounds.bottom - rect.bottom);

        const offset = this.suuhaa_offsets.get(item) || { x: 0, y: 0 };
        offset.x += clampedX;
        offset.y += clampedY;
        this.suuhaa_offsets.set(item, offset);
        this.suuhaa_applyTransform(item);
      }

      // top/left は触らず、transform: translate() だけで位置を管理する
      // (-50% は「% で指定した位置を、リンゴの中心にそろえる」ための補正)
      suuhaa_applyTransform(item) {
        const offset = this.suuhaa_offsets.get(item) || { x: 0, y: 0 };
        item.style.transform = `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`;
      }

      // ステージの幅が変わったら、移動量も同じ比率で拡縮して封筒との位置関係を保つ
      suuhaa_onResize(stage) {
        const width = stage.getBoundingClientRect().width;
        const prev = this.suuhaa_stageWidth;
        this.suuhaa_stageWidth = width;
        if (!prev || !width || prev === width) return;

        const ratio = width / prev;
        this.suuhaa_offsets.forEach((offset, item) => {
          offset.x *= ratio;
          offset.y *= ratio;
          this.suuhaa_applyTransform(item);
          this.suuhaa_moveBy(item, 0, 0); // はみ出していたら中へ戻す
        });
      }
    }

    customElements.define('suuhaa-hero-snail-mail', SuuhaaHeroSnailMail);
  }
})();

/* --------------------------------------------------------------------------
   <suuhaa-menu-panel>: ハンバーガーで開閉するメニューパネル
   ・開閉は data-suuhaa-open 属性で管理(動きは CSS の transition)
   ・Esc / 閉じるボタンで閉じる。開いている間は Tab キーがパネルの中だけを回る
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  // 設定値はここに一元管理
  const suuhaa_menuConfig = {
    lockClass: 'suuhaa-menu-open', // 開いている間 <html> に付けてスクロールを止める
    openAttribute: 'data-suuhaa-open',
    desktopQuery: '(min-width: 990px)', // Dawn と同じデスクトップ幅
    focusable: 'a[href], button:not([disabled])',
  };

  if (!customElements.get('suuhaa-menu-panel')) {
    class SuuhaaMenuPanel extends HTMLElement {
      constructor() {
        super();
        this.suuhaa_isOpen = false;
        this.suuhaa_abort = null;
      }

      connectedCallback() {
        this.suuhaa_toggle = this.querySelector('[data-suuhaa-menu-toggle]');
        this.suuhaa_panel = this.querySelector('[data-suuhaa-menu-panel]');
        this.suuhaa_closeButton = this.querySelector('[data-suuhaa-menu-close]');
        if (!this.suuhaa_toggle || !this.suuhaa_panel || !this.suuhaa_closeButton) return;

        this.suuhaa_abort = new AbortController();
        const signal = this.suuhaa_abort.signal;

        this.suuhaa_toggle.addEventListener('click', () => this.suuhaa_open(), { signal });
        this.suuhaa_closeButton.addEventListener('click', () => this.suuhaa_close(), { signal });
        this.suuhaa_panel.addEventListener('keydown', (e) => this.suuhaa_onKeyDown(e), { signal });
        // ページ内リンク(#...)を押したときもパネルを閉じる
        this.suuhaa_panel.addEventListener(
          'click',
          (e) => {
            const link = e.target.closest('a[href^="#"]');
            if (link) this.suuhaa_close(false);
          },
          { signal }
        );
        // 戻るボタンで戻ってきたときに開いたままにならないように
        window.addEventListener('pageshow', () => this.suuhaa_close(false), { signal });

        // タブレット幅までのメニューは、デスクトップ幅になったら閉じる(閉じ忘れでスクロールが止まるのを防ぐ)
        if (this.dataset.breakpoint !== 'desktop') {
          const mediaQuery = window.matchMedia(suuhaa_menuConfig.desktopQuery);
          mediaQuery.addEventListener('change', (e) => e.matches && this.suuhaa_close(false), { signal });
        }
      }

      // テーマエディターでセクションが消えたとき等の後片付け
      disconnectedCallback() {
        if (this.suuhaa_abort) {
          this.suuhaa_abort.abort(); // 登録したイベントリスナーをすべて解除
          this.suuhaa_abort = null;
        }
        if (this.suuhaa_isOpen) {
          document.documentElement.classList.remove(suuhaa_menuConfig.lockClass);
        }
        this.suuhaa_isOpen = false;
      }

      suuhaa_open() {
        if (this.suuhaa_isOpen) return;
        this.suuhaa_isOpen = true;
        this.setAttribute(suuhaa_menuConfig.openAttribute, '');
        this.suuhaa_toggle.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add(suuhaa_menuConfig.lockClass);
        // 表示が切り替わってから、フォーカスをパネル自身に移す
        // (×ボタンに直接移すと、タップしただけでフォーカスの枠が出てしまうため。Tabキーを押すと×から順に進む)
        requestAnimationFrame(() => this.suuhaa_panel.focus({ preventScroll: true }));
      }

      suuhaa_close(returnFocus = true) {
        if (!this.suuhaa_isOpen) return;
        this.suuhaa_isOpen = false;
        this.removeAttribute(suuhaa_menuConfig.openAttribute);
        this.suuhaa_toggle.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove(suuhaa_menuConfig.lockClass);
        if (returnFocus) this.suuhaa_toggle.focus({ preventScroll: true });
      }

      suuhaa_onKeyDown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.suuhaa_close();
          return;
        }
        if (event.key !== 'Tab') return;

        // Tab キーがパネルの外に出ないよう、先頭と末尾でぐるっと回す
        const items = this.suuhaa_panel.querySelectorAll(suuhaa_menuConfig.focusable);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    customElements.define('suuhaa-menu-panel', SuuhaaMenuPanel);
  }
})();

/* --------------------------------------------------------------------------
   <suuhaa-faq>: アコーディオン形式のFAQ
   ・開閉は項目の data-suuhaa-open 属性と、ボタンの aria-expanded で管理(動きは CSS の transition)
   ・テーマエディターでブロックを選ぶと、その質問が開く(shopify:block:select)
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  // 設定値はここに一元管理
  const suuhaa_faqConfig = {
    openAttribute: 'data-suuhaa-open',
    itemSelector: '[data-suuhaa-faq-item]',
    triggerSelector: '[data-suuhaa-faq-trigger]',
  };

  if (!customElements.get('suuhaa-faq')) {
    class SuuhaaFaq extends HTMLElement {
      constructor() {
        super();
        this.suuhaa_abort = null;
      }

      connectedCallback() {
        this.suuhaa_abort = new AbortController();
        const signal = this.suuhaa_abort.signal;

        this.querySelectorAll(suuhaa_faqConfig.itemSelector).forEach((item) => {
          const trigger = item.querySelector(suuhaa_faqConfig.triggerSelector);
          if (trigger) {
            trigger.addEventListener('click', () => this.suuhaa_toggle(item), { signal });
          }
        });

        // テーマエディターでブロックを選んだとき、その質問を開く
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const item = event.target.closest(suuhaa_faqConfig.itemSelector);
            if (item) this.suuhaa_set(item, true);
          },
          { signal }
        );
      }

      // テーマエディターでセクションが消えたとき等の後片付け
      disconnectedCallback() {
        if (this.suuhaa_abort) {
          this.suuhaa_abort.abort(); // 登録したイベントリスナーをすべて解除
          this.suuhaa_abort = null;
        }
      }

      suuhaa_toggle(item) {
        const willOpen = !item.hasAttribute(suuhaa_faqConfig.openAttribute);
        // 「同時に開くのは1つだけ」のときは、ほかを閉じる
        if (willOpen && this.dataset.single === 'true') {
          this.querySelectorAll(suuhaa_faqConfig.itemSelector).forEach((other) => {
            if (other !== item) this.suuhaa_set(other, false);
          });
        }
        this.suuhaa_set(item, willOpen);
      }

      suuhaa_set(item, open) {
        item.toggleAttribute(suuhaa_faqConfig.openAttribute, open);
        const trigger = item.querySelector(suuhaa_faqConfig.triggerSelector);
        if (trigger) trigger.setAttribute('aria-expanded', String(open));
      }
    }

    customElements.define('suuhaa-faq', SuuhaaFaq);
  }
})();

/* --------------------------------------------------------------------------
   透明ヘッダーの「重なり分の高さ」を計算する (--suuhaa-header-offset)
   ・ヘッダーはページの一番上(y=0)から始まり、お知らせバーはその手前に重なって見える
   ・そのため、ページ本文(main)の先頭からヘッダー下端までの距離 =
     「ヘッダーの高さ - 本文の開始位置(お知らせバーの高さ)」だけ余白を足せばよい
   ・ABOUT / FAQ をページの先頭に置いたときの上の余白に使われる(CSS側: .suuhaa-*--first)
   ・JSが動くまでは、CSS側の初期値(--header-height)がそのまま使われる
   -------------------------------------------------------------------------- */
(function () {
  const suuhaa_headerOffsetConfig = {
    headerSelector: '.section-header', // Dawnのヘッダーセクション
    mainSelector: 'main',
    variableName: '--suuhaa-header-offset',
  };

  let suuhaa_resizeObserver = null;

  function suuhaa_updateHeaderOffset() {
    const header = document.querySelector(suuhaa_headerOffsetConfig.headerSelector);
    const main = document.querySelector(suuhaa_headerOffsetConfig.mainSelector);
    if (!header || !main) return;

    // ヘッダーを本文に重ねていない(通常の並び)ときは何もしない
    const position = window.getComputedStyle(header).position;
    if (position !== 'absolute' && position !== 'fixed') {
      document.documentElement.style.removeProperty(suuhaa_headerOffsetConfig.variableName);
      return;
    }

    // 本文の先頭位置(スクロール量を足して、ページ先頭からの距離にする)
    const mainTop = main.getBoundingClientRect().top + window.scrollY;
    const offset = Math.max(0, header.offsetHeight - mainTop);
    document.documentElement.style.setProperty(suuhaa_headerOffsetConfig.variableName, offset + 'px');
  }

  function suuhaa_initHeaderOffset() {
    suuhaa_updateHeaderOffset();

    // ヘッダー・本文の大きさが変わったとき(画像の読み込み、メニュー変更など)に再計算
    if (suuhaa_resizeObserver) suuhaa_resizeObserver.disconnect();
    if ('ResizeObserver' in window) {
      suuhaa_resizeObserver = new ResizeObserver(suuhaa_updateHeaderOffset);
      const header = document.querySelector(suuhaa_headerOffsetConfig.headerSelector);
      const main = document.querySelector(suuhaa_headerOffsetConfig.mainSelector);
      if (header) suuhaa_resizeObserver.observe(header);
      if (main) suuhaa_resizeObserver.observe(main);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', suuhaa_initHeaderOffset);
  } else {
    suuhaa_initHeaderOffset();
  }
  window.addEventListener('load', suuhaa_updateHeaderOffset);
  window.addEventListener('resize', suuhaa_updateHeaderOffset);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(suuhaa_updateHeaderOffset);

  // テーマエディターでセクションを追加・変更したときも再計算
  document.addEventListener('shopify:section:load', suuhaa_initHeaderOffset);
  document.addEventListener('shopify:section:unload', suuhaa_updateHeaderOffset);
})();

/* --------------------------------------------------------------------------
   suuhaa-image-modal: 画像をクリックすると説明のモーダルがふわっと開く
   ・Custom Element <suuhaa-image-modal> (sections/suuhaa-image-modal.liquid)
   ・モーダルは <dialog> を使う(Escキー・フォーカスの閉じ込めはブラウザの標準機能)
   ・開閉のアニメーションは CSS の transition。ここでは is-open クラスの付け外しと、
     閉じるアニメーションが終わってから dialog を閉じる待ち時間を管理する
   -------------------------------------------------------------------------- */
(function () {
  // 設定は一箇所にまとめる(duration は CSS の --suuhaa-modal-duration と同じ値にすること)
  const suuhaa_modalConfig = {
    openSelector: '[data-suuhaa-modal-open]',
    dialogSelector: '[data-suuhaa-modal]',
    closeSelector: '[data-suuhaa-modal-close]',
    openClass: 'is-open',
    lockClass: 'suuhaa-modal-open',
    duration: 400, // ms
    // 登場アニメーション(順番にスッと表示)。見た目の時間は CSS の .is-popped と同じ値にすること
    popReadyClass: 'is-pop-ready',
    popItemClass: 'is-popped',
    popStageSelector: '.suuhaa-image-modal__stage',
    popItemSelector: '.suuhaa-image-modal__item',
    popThreshold: 0.2, // ステージが何割見えたら始めるか
    popAnimationTime: 1000, // ms(1枚あたりのアニメーションの長さ。後片付けの待ち時間に使う)
    popDefaultInterval: 300, // ms
    popDefaultStart: 300, // ms(1枚目が出るまでの待ち時間)
  };

  if (!customElements.get('suuhaa-image-modal')) {
    class SuuhaaImageModal extends HTMLElement {
      connectedCallback() {
        this.suuhaa_abort = new AbortController();
        const signal = this.suuhaa_abort.signal;
        this.suuhaa_active = null; // いま開いている dialog
        this.suuhaa_trigger = null; // キーボードで開いたときだけ、閉じたあとにフォーカスを戻すボタン
        this.suuhaa_timer = null;
        this.suuhaa_popTimer = null;
        this.suuhaa_observer = null;

        this.suuhaa_setupPop();

        // 画像(ボタン)をクリック → 対応するモーダルを開く
        this.querySelectorAll(suuhaa_modalConfig.openSelector).forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              // event.detail === 0 はキーボード操作(Enter / Space)で押されたとき。
              // マウス・タッチで開いたときはフォーカスを戻さない(戻すと画像に枠線が出てしまうため)
              const viaKeyboard = event.detail === 0;
              this.suuhaa_open(button.getAttribute('data-suuhaa-modal-open'), viaKeyboard ? button : null);
            },
            { signal }
          );
        });

        this.querySelectorAll(suuhaa_modalConfig.dialogSelector).forEach((dialog) => {
          // Escキー: ブラウザ標準の即時クローズをやめて、ふわっと閉じる
          dialog.addEventListener(
            'cancel',
            (event) => {
              event.preventDefault();
              this.suuhaa_close();
            },
            { signal }
          );
          // 背景・×ボタンのクリックで閉じる
          dialog.addEventListener(
            'click',
            (event) => {
              if (event.target === dialog || event.target.closest(suuhaa_modalConfig.closeSelector)) {
                this.suuhaa_close();
              }
            },
            { signal }
          );
        });

        // テーマエディターでブロックを選んだら、そのモーダルを開く
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const id = event.target.getAttribute('data-suuhaa-modal-open');
            if (id) this.suuhaa_open(id, null);
          },
          { signal }
        );
        this.addEventListener('shopify:block:deselect', () => this.suuhaa_close(), { signal });
      }

      // 登場アニメーション: ステージが画面に入ったら、上の画像から順に is-popped を付ける
      suuhaa_setupPop() {
        if (!this.hasAttribute('data-suuhaa-pop')) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const stage = this.querySelector(suuhaa_modalConfig.popStageSelector);
        if (reduced || !stage || !('IntersectionObserver' in window)) return;

        // 先に画像を隠す(CSS)。表示のきっかけは IntersectionObserver
        this.classList.add(suuhaa_modalConfig.popReadyClass);
        this.suuhaa_observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.suuhaa_observer.disconnect();
            this.suuhaa_observer = null;
            this.suuhaa_playPop();
          },
          { threshold: suuhaa_modalConfig.popThreshold }
        );
        this.suuhaa_observer.observe(stage);
      }

      suuhaa_playPop() {
        const interval = parseInt(this.getAttribute('data-suuhaa-pop-interval'), 10) || suuhaa_modalConfig.popDefaultInterval;
        // 0ms も有効な値なので、数値でなかったときだけ初期値を使う
        const startAttr = parseInt(this.getAttribute('data-suuhaa-pop-start'), 10);
        const start = Number.isNaN(startAttr) ? suuhaa_modalConfig.popDefaultStart : startAttr;
        const items = Array.from(this.querySelectorAll(suuhaa_modalConfig.popItemSelector));

        // 画面上の縦位置(中心)が上にあるものから順番に並べる
        const centerY = (el) => {
          const rect = el.getBoundingClientRect();
          return rect.top + rect.height / 2;
        };
        items.sort((a, b) => centerY(a) - centerY(b));

        items.forEach((item, index) => {
          item.style.setProperty('--suuhaa-pop-delay', (start + index * interval) + 'ms');
          item.classList.add(suuhaa_modalConfig.popItemClass);
        });

        // 全部終わったら後片付け(ホバー演出などの通常の動きに戻す。見た目は変わらない)
        const total = start + Math.max(items.length - 1, 0) * interval + suuhaa_modalConfig.popAnimationTime;
        this.suuhaa_popTimer = setTimeout(() => {
          this.classList.remove(suuhaa_modalConfig.popReadyClass);
          items.forEach((item) => {
            item.classList.remove(suuhaa_modalConfig.popItemClass);
            item.style.removeProperty('--suuhaa-pop-delay');
          });
        }, total);
      }

      suuhaa_open(id, trigger) {
        if (this.suuhaa_active) return;
        const dialog = this.querySelector('#' + CSS.escape(id));
        if (!dialog) return;

        clearTimeout(this.suuhaa_timer);
        this.suuhaa_active = dialog;
        this.suuhaa_trigger = trigger;

        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        document.documentElement.classList.add(suuhaa_modalConfig.lockClass);

        // 表示された次のフレームで is-open を付けて、transition を動かす
        requestAnimationFrame(() => {
          requestAnimationFrame(() => dialog.classList.add(suuhaa_modalConfig.openClass));
        });
      }

      suuhaa_close() {
        const dialog = this.suuhaa_active;
        if (!dialog) return;

        dialog.classList.remove(suuhaa_modalConfig.openClass);
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const wait = reduced ? 0 : suuhaa_modalConfig.duration;

        // 閉じるアニメーションが終わってから dialog を閉じる
        this.suuhaa_timer = setTimeout(() => this.suuhaa_finishClose(dialog), wait);
      }

      suuhaa_finishClose(dialog) {
        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
        document.documentElement.classList.remove(suuhaa_modalConfig.lockClass);
        const trigger = this.suuhaa_trigger;
        this.suuhaa_active = null;
        this.suuhaa_trigger = null;
        if (trigger && typeof trigger.focus === 'function') trigger.focus();
      }

      disconnectedCallback() {
        if (this.suuhaa_abort) this.suuhaa_abort.abort();
        clearTimeout(this.suuhaa_timer);
        clearTimeout(this.suuhaa_popTimer);
        if (this.suuhaa_observer) this.suuhaa_observer.disconnect();
        // セクションが消えたとき(エディターでの削除など)は、アニメーションなしで後片付けする
        if (this.suuhaa_active) {
          this.suuhaa_active.classList.remove(suuhaa_modalConfig.openClass);
          this.suuhaa_active = null;
        }
        document.documentElement.classList.remove(suuhaa_modalConfig.lockClass);
      }
    }

    customElements.define('suuhaa-image-modal', SuuhaaImageModal);
  }
})();

/* --------------------------------------------------------------------------
   suuhaa-postcard-name: 名入れポストカード用の名前入力欄(商品ページ)
   ・Custom Element <suuhaa-postcard-name> (snippets/suuhaa-postcard-name.liquid)
   ・入力した文字を自動で大文字にする(日本語入力の変換中は触らない)
   ・Dawn の商品フォームは novalidate(ブラウザ標準の必須チェックが動かない)なので、
     未入力のまま「カートに追加」「今すぐ購入」を押したときのチェックはここで行う
   -------------------------------------------------------------------------- */
(function () {
  const suuhaa_postcardConfig = {
    inputSelector: '[data-suuhaa-postcard-input]',
    errorSelector: '[data-suuhaa-postcard-error]',
    invalidClass: 'suuhaa-postcard-name--invalid',
    // 商品情報の外枠 / カートに追加するフォーム / 「今すぐ購入」などの決済ボタンの外枠
    rootSelector: 'product-info',
    formSelector: 'form[data-type="add-to-cart-form"]',
    paymentSelector: '.shopify-payment-button',
  };

  if (!customElements.get('suuhaa-postcard-name')) {
    class SuuhaaPostcardName extends HTMLElement {
      connectedCallback() {
        this.suuhaa_abort = new AbortController();
        const signal = this.suuhaa_abort.signal;
        const cfg = suuhaa_postcardConfig;

        this.suuhaa_input = this.querySelector(cfg.inputSelector);
        this.suuhaa_error = this.querySelector(cfg.errorSelector);
        if (!this.suuhaa_input) return;

        // この入力欄が属する商品情報(同じページに複数あっても、自分の商品だけを対象にする)
        this.suuhaa_root = this.closest(cfg.rootSelector) || this.closest('.shopify-section') || document;
        this.suuhaa_linkForm();

        // 入力 → 大文字へ。日本語入力の変換中(isComposing)は、確定してから変換する
        this.suuhaa_input.addEventListener(
          'input',
          (event) => {
            if (event.isComposing) return;
            this.suuhaa_toUpper();
            this.suuhaa_hideError();
          },
          { signal }
        );
        this.suuhaa_input.addEventListener('compositionend', () => this.suuhaa_toUpper(), { signal });

        // 「カートに追加」(フォーム送信): 未入力なら止める。
        // 親要素の capture で受けるので、Dawn の送信処理(product-form.js)より先に動く
        this.suuhaa_root.addEventListener(
          'submit',
          (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement) || !form.matches(cfg.formSelector)) return;
            if (!this.suuhaa_validate()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          },
          { capture: true, signal }
        );

        // 「今すぐ購入」などの決済ボタン: フォームを通らないので、クリック・Enter/Spaceを先に止める
        const guard = (event) => {
          const onPayment = event.composedPath().some((el) => el.matches && el.matches(cfg.paymentSelector));
          if (!onPayment) return;
          if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
          if (!this.suuhaa_validate()) {
            event.preventDefault();
            event.stopPropagation();
          }
        };
        this.suuhaa_root.addEventListener('click', guard, { capture: true, signal });
        this.suuhaa_root.addEventListener('keydown', guard, { capture: true, signal });
      }

      // 入力欄を、カートに追加するフォームに紐づける(フォームの外にあっても送信データに入る)
      suuhaa_linkForm() {
        const form = this.suuhaa_root.querySelector(suuhaa_postcardConfig.formSelector);
        // ※ form.id は、フォーム内の <input name="id"> に上書きされてしまうので getAttribute を使う
        const formId = form && form.getAttribute('id');
        if (formId) this.suuhaa_input.setAttribute('form', formId);
      }

      // 大文字に変換。カーソル位置がジャンプしないよう、位置を覚えて戻す
      suuhaa_toUpper() {
        const input = this.suuhaa_input;
        const upper = input.value.toUpperCase();
        if (upper === input.value) return;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        input.value = upper;
        if (start !== null && end !== null) input.setSelectionRange(start, end);
      }

      // 空白だけの入力も未入力として扱う。OKなら前後の空白を取り除いて大文字にそろえる
      suuhaa_validate() {
        const value = this.suuhaa_input.value.trim().toUpperCase();
        if (value === '') {
          this.suuhaa_showError();
          return false;
        }
        this.suuhaa_input.value = value;
        this.suuhaa_hideError();
        return true;
      }

      suuhaa_showError() {
        this.classList.add(suuhaa_postcardConfig.invalidClass);
        this.suuhaa_input.setAttribute('aria-invalid', 'true');
        if (this.suuhaa_error) this.suuhaa_error.hidden = false;
        this.suuhaa_input.focus();
      }

      suuhaa_hideError() {
        this.classList.remove(suuhaa_postcardConfig.invalidClass);
        this.suuhaa_input.removeAttribute('aria-invalid');
        if (this.suuhaa_error) this.suuhaa_error.hidden = true;
      }

      disconnectedCallback() {
        if (this.suuhaa_abort) this.suuhaa_abort.abort();
      }
    }

    customElements.define('suuhaa-postcard-name', SuuhaaPostcardName);
  }
})();
